import moment from 'moment';
import { getDistance } from 'geolib';
import LocalStorageService from '../services/LocalStorageService';

export interface ComplianceStatus {
  status: 'green' | 'red' | 'gray';
  tooltip: string;
  severity: string;
}

export const getCheckinStatus = (checkins: any[], scheduledStartUTC: string): ComplianceStatus => {
  const checkin64 = checkins.find((checkin: any) => checkin.checkinType === 64);
  if (!checkin64) {
    return {
      status: 'gray',
      severity: '',
      tooltip: 'No check-in recorded yet'
    };
  }

  const scheduledStart = moment.utc(scheduledStartUTC?.replace('Z', ''));
  const checkinTime = moment.utc(checkin64.createdOnUtc?.replace('Z', ''));
  const diffInMinutes = scheduledStart.diff(checkinTime, 'minutes');
  const isCompliant = diffInMinutes >= 30;
  const absMinutes = Math.abs(diffInMinutes);
  const direction = diffInMinutes >= 0 ? 'before' : 'after';
  const hours = Math.floor(absMinutes / 60);
  const minutes = absMinutes % 60;
  const timeStr = [
    hours > 0 ? `${hours}h` : null,
    minutes > 0 ? `${minutes}m` : null
  ].filter(Boolean).join(' ');

  return {
    status: isCompliant ? 'green' : 'red',
    severity: '',
    tooltip: isCompliant
      ? `Driver committed to shift ${timeStr} ${direction} scheduled start time - Compliant`
      : `Driver committed to shift ${timeStr} ${direction} scheduled start time - Non-compliant`
  };
};

export const getLocationStatus = (checkins: any[], jobsiteLocationLat: string, jobsiteLocationLng: string, scheduledStartTime: string): ComplianceStatus => {
  const checkin128 = checkins.find((checkin: any) => checkin.checkinType === 128);
  if (!checkin128) {
    return {
      status: 'gray',
      severity: '',
      tooltip: 'No location check-in recorded yet'
    };
  }

  if (checkin128 && jobsiteLocationLat && jobsiteLocationLng) {
    // const checkinLocation = checkin128.location;
    const scheduledStart = moment.utc(scheduledStartTime?.replace('Z', ''));
    const checkinTime = moment.utc(checkin128.createdOnUTC?.replace('Z', ''));

    const distance = getDistance(
      { latitude: checkin128.latitude, longitude: checkin128.longitude },
      { latitude: jobsiteLocationLat, longitude: jobsiteLocationLng }
    ) / 1000;

    const isCompliant = distance <= 3 && checkinTime.isBefore(scheduledStart);

    return {
      status: isCompliant ? 'green' : 'red',
      severity: '',
      tooltip: isCompliant
        ? 'Driver is within 3km of jobsite location and arrived before scheduled start - Compliant'
        : 'Driver is either outside 3km radius of jobsite or arrived after scheduled start - Non-compliant'
    };
  }

  return {
    status: 'red',
    severity: '',
    tooltip: 'Location compliance cannot be verified - No jobsite location provided'
  };
};

export const getPrestartStatus = (checkins: any[]): ComplianceStatus => {
  const localStorageService = LocalStorageService.getService();
  const companyProfile = localStorageService.getCompanyProfile();

  const checkin64Exists = checkins.some((checkin: any) => checkin.checkinType === 64);
  const checkin128Exists = checkins.some((checkin: any) => checkin.checkinType === 128);

  if (!checkin64Exists) {
    return {
      status: 'gray',
      severity: '',
      tooltip: `${companyProfile.name} precheck - Pending`
    };
  }

  const isCompliant = checkin64Exists && checkin128Exists;

  return {
    status: isCompliant ? 'green' : 'red',
    severity: '',
    tooltip: isCompliant
      ? `${companyProfile.name} precheck - Done`
      : `${companyProfile.name} precheck - Incomplete`
  };
};

export const getBreakStatus = (
  startedOnUTC: string | null,
  shiftBreaks: any[],
  endedOnUTC: string | null
): ComplianceStatus => {
  function getSeverity(hours: number): string {
    if (hours >= 4.5) return 'high';
    if (hours > 4) return 'warning';
    return '';
  }

  function getMessage(hours: number, hasBreak: boolean): string {
    const severity = getSeverity(hours);

    if (!hasBreak) {
      if (severity === '') return 'Shift <4 hrs – No break needed';
      if (severity === 'warning') return 'Warning – 4+ hrs driving, break due soon';
      if (severity === 'high') return `Driving ${hours} hrs – Break overdue (non-compliant)`;
    } else {
      if (severity === '') return 'Break within 4 hrs – Compliant';
      if (severity === 'warning') return 'Break after 4–4.5 hrs – Still compliant';
      if (severity === 'high') return 'Break after 4.5 hrs – Non-compliant';
    }

    return 'Break status could not be determined';
  }

  if (!startedOnUTC) {
    return {
      status: 'gray',
      severity: '',
      tooltip: 'Break status not applicable - Shift not started'
    };
  }

  const shiftStart = moment.utc(startedOnUTC?.replace('Z', ''));
  const shiftEnd = moment.utc(endedOnUTC?.replace('Z', ''));
  const breakStart = shiftBreaks?.[0]?.startedOnActualUTC
    ? moment.utc(shiftBreaks[0].startedOnActualUTC?.replace('Z', ''))
    : null;

  if (!breakStart) {
    const now = moment().utc();
    const hoursSinceStart = shiftEnd.isValid()
      ? shiftEnd.diff(shiftStart, 'hours')
      : now.diff(shiftStart, 'hours');

    return {
      status: hoursSinceStart <= 4.5 ? 'green' : 'red',
      severity: getSeverity(hoursSinceStart),
      tooltip: getMessage(hoursSinceStart, false)
    };
  }

  const hoursBeforeBreak = breakStart.diff(shiftStart, 'hours');

  return {
    status: hoursBeforeBreak <= 4.5 ? 'green' : 'red',
    severity: getSeverity(hoursBeforeBreak),
    tooltip: getMessage(hoursBeforeBreak, true)
  };
};

export const getIconContainerStyle = (status: 'green' | 'red' | 'gray') => {
  const backgroundColors = {
    green: '#d3f9d8',
    red: '#ffe3e3',
    gray: '#f1f1f1',
  };

  return {
    backgroundColor: backgroundColors[status],
    borderRadius: '50%',
    padding: 6,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 30,
    height: 30,
  };
};

export const getIconColor = (status: 'green' | 'red' | 'gray') => {
  switch (status) {
    case 'green':
      return 'green';
    case 'red':
      return '#dc3545';
    case 'gray':
    default:
      return '#6c757d';
  }
};

export const iconStyle = {
  fontSize: 18,
};
